import React, { useState } from 'react';
import { Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

/**
 * Analog clock-face time picker.
 * - Mode 1: pick the hour (12 numbers around the dial; AM/PM toggle)
 * - Mode 2: pick the minute (12 ticks at 0, 5, 10, ... 55)
 *
 * Emits a "HH:mm" string (24h) via onChange when both hour + minute have been chosen.
 *
 * Props:
 *  - value: "HH:mm" or empty
 *  - onChange: (newValue) => void
 *  - hasError: bool to apply error styling
 */
const ClockTimePicker = ({ value, onChange, hasError = false }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('hour'); // 'hour' | 'minute'

  // Parse current value (24h "HH:mm")
  const initialHour24 = value ? parseInt(value.split(':')[0], 10) : null;
  const initialMinute = value ? parseInt(value.split(':')[1], 10) : null;

  const [hour24, setHour24] = useState(initialHour24);
  const [minute, setMinute] = useState(initialMinute);

  const handleHourPick = (h12, isPM) => {
    let h = h12 % 12; // 12 -> 0
    if (isPM) h += 12;
    setHour24(h);
    setMode('minute');
  };

  const handleMinutePick = (m) => {
    setMinute(m);
    if (hour24 !== null) {
      const hh = String(hour24).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      onChange(`${hh}:${mm}`);
      setOpen(false);
    }
  };

  const handleOpenChange = (next) => {
    setOpen(next);
    if (next) {
      // Reset to hour-pick when opening
      setMode(hour24 === null ? 'hour' : 'hour');
    }
  };

  const displayLabel = value || 'Pick a time';

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={`w-full justify-start text-left font-normal h-12 rounded-xl bg-black/30 border-white/10 text-white hover:bg-black/40 hover:text-white px-4 ${
            hasError ? 'border-red-500' : ''
          } ${!value ? 'text-gray-500' : ''}`}
          data-testid="transport-time-trigger"
        >
          <Clock className="mr-3 h-5 w-5 text-amber-500" />
          {displayLabel}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="w-auto p-4 bg-[#1a1a1a] border-white/10 z-[80]"
        align="start"
      >
        <div className="flex flex-col items-center gap-3">
          {/* Header — selected time preview + mode toggle */}
          <div className="flex items-center gap-1 text-2xl font-bold">
            <button
              type="button"
              onClick={() => setMode('hour')}
              className={`px-2 py-1 rounded transition-colors ${
                mode === 'hour' ? 'text-amber-500' : 'text-gray-400 hover:text-white'
              }`}
              data-testid="clock-mode-hour"
            >
              {hour24 !== null ? String(hour24).padStart(2, '0') : '--'}
            </button>
            <span className="text-gray-500">:</span>
            <button
              type="button"
              onClick={() => setMode('minute')}
              disabled={hour24 === null}
              className={`px-2 py-1 rounded transition-colors ${
                mode === 'minute' ? 'text-amber-500' : 'text-gray-400 hover:text-white'
              } disabled:opacity-50`}
              data-testid="clock-mode-minute"
            >
              {minute !== null ? String(minute).padStart(2, '0') : '--'}
            </button>
          </div>

          {/* The dial */}
          {mode === 'hour' ? (
            <HourDial
              hour24={hour24}
              onPick={handleHourPick}
            />
          ) : (
            <MinuteDial minute={minute} onPick={handleMinutePick} />
          )}

          <p className="text-gray-500 text-xs">
            {mode === 'hour' ? 'Tap an hour' : 'Tap a minute'}
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const DIAL_SIZE = 232;
const CENTER = DIAL_SIZE / 2;
const RADIUS_OUTER = 92; // PM hours 13-00 + minutes
const RADIUS_INNER = 60; // AM hours 1-12

const polar = (cx, cy, r, angleDeg) => {
  const a = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
};

const HourDial = ({ hour24, onPick }) => {
  // Two rings: outer = AM (1-12), inner = PM (13-00)
  // For visual clarity: outer ring shows 1..12, inner shows 13..00 (or "13-24")
  const amHours = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]; // index 0 at top = 12
  const pmHours = [0, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23]; // index 0 at top = 0/24

  return (
    <svg
      width={DIAL_SIZE}
      height={DIAL_SIZE}
      viewBox={`0 0 ${DIAL_SIZE} ${DIAL_SIZE}`}
      className="touch-none select-none"
      data-testid="hour-dial"
    >
      {/* Dial face */}
      <circle cx={CENTER} cy={CENTER} r={DIAL_SIZE / 2 - 4} fill="#0a0a0a" stroke="rgba(255,255,255,0.08)" />
      {/* Center dot */}
      <circle cx={CENTER} cy={CENTER} r={3} fill="#F59E0B" />

      {/* Outer ring (AM) */}
      {amHours.map((h, i) => {
        const { x, y } = polar(CENTER, CENTER, RADIUS_OUTER, i * 30);
        const selected = hour24 !== null && (hour24 % 12 || 12) === h && hour24 < 12;
        return (
          <g key={`am-${h}`} onClick={() => onPick(h, false)} className="cursor-pointer">
            {selected && <circle cx={x} cy={y} r={18} fill="#F59E0B" />}
            <text
              x={x}
              y={y}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize="15"
              fontWeight={selected ? '700' : '500'}
              fill={selected ? '#000' : '#fff'}
              style={{ pointerEvents: 'none' }}
            >
              {h}
            </text>
            <circle cx={x} cy={y} r={18} fill="transparent" />
          </g>
        );
      })}

      {/* Inner ring (PM 13-23 + 00) */}
      {pmHours.map((h, i) => {
        const { x, y } = polar(CENTER, CENTER, RADIUS_INNER, i * 30);
        const selected = hour24 !== null && hour24 === h && hour24 >= 12;
        const label = h === 0 ? '00' : String(h);
        return (
          <g key={`pm-${h}`} onClick={() => onPick(h, true)} className="cursor-pointer">
            {selected && <circle cx={x} cy={y} r={15} fill="#F59E0B" />}
            <text
              x={x}
              y={y}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize="12"
              fontWeight={selected ? '700' : '500'}
              fill={selected ? '#000' : '#9ca3af'}
              style={{ pointerEvents: 'none' }}
            >
              {label}
            </text>
            <circle cx={x} cy={y} r={15} fill="transparent" />
          </g>
        );
      })}
    </svg>
  );
};

const MinuteDial = ({ minute, onPick }) => {
  const minutes = Array.from({ length: 12 }, (_, i) => i * 5); // 0, 5, ... 55
  return (
    <svg
      width={DIAL_SIZE}
      height={DIAL_SIZE}
      viewBox={`0 0 ${DIAL_SIZE} ${DIAL_SIZE}`}
      className="touch-none select-none"
      data-testid="minute-dial"
    >
      <circle cx={CENTER} cy={CENTER} r={DIAL_SIZE / 2 - 4} fill="#0a0a0a" stroke="rgba(255,255,255,0.08)" />
      <circle cx={CENTER} cy={CENTER} r={3} fill="#F59E0B" />

      {minutes.map((m, i) => {
        const { x, y } = polar(CENTER, CENTER, RADIUS_OUTER, i * 30);
        const selected = minute === m;
        const label = String(m).padStart(2, '0');
        return (
          <g key={m} onClick={() => onPick(m)} className="cursor-pointer">
            {selected && <circle cx={x} cy={y} r={18} fill="#F59E0B" />}
            <text
              x={x}
              y={y}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize="15"
              fontWeight={selected ? '700' : '500'}
              fill={selected ? '#000' : '#fff'}
              style={{ pointerEvents: 'none' }}
            >
              {label}
            </text>
            <circle cx={x} cy={y} r={18} fill="transparent" />
          </g>
        );
      })}
    </svg>
  );
};

export default ClockTimePicker;
